import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
    Check,
    Clock,
    Activity,
    Minus,
    Plus,
    ArrowUp,
    ArrowDown,
    Hash,
} from "lucide-react";
import { useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_API_URL } from "../helpers/Config";
import { TriggerBalanceUpdate } from "../helpers/TriggerBalanceUpdate";
import moment from "moment";
import { io } from "socket.io-client";

/* --------------------------------------------
  TradingView Advanced Chart Widget (Embed)
--------------------------------------------- */
const TradingViewAdvancedChart = ({
    symbol = "BINANCE:BTCUSDT",
    theme = "dark",
    height = 260,
    interval = "1",
}) => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        containerRef.current.innerHTML = "";

        const wrapper = document.createElement("div");
        wrapper.className = "tradingview-widget-container";
        wrapper.style.width = "100%";
        wrapper.style.height = "100%";

        const widgetDiv = document.createElement("div");
        widgetDiv.className = "tradingview-widget-container__widget";
        widgetDiv.style.width = "100%";
        widgetDiv.style.height = "100%";

        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src =
            "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
        script.innerHTML = JSON.stringify({
            autosize: true,
            symbol,
            interval,
            theme,
            locale: "en",
            allow_symbol_change: false,
            calendar: false,
            hide_side_toolbar: true,
            hide_top_toolbar: false,
            hide_legend: false,
            support_host: "https://www.tradingview.com",
            style: "2",
        });

        wrapper.appendChild(widgetDiv);
        wrapper.appendChild(script);
        containerRef.current.appendChild(wrapper);

        return () => {
            if (containerRef.current) containerRef.current.innerHTML = "";
        };
    }, [symbol, theme, interval]);

    return <div ref={containerRef} style={{ width: "100%", height }} />;
};

/* -------------------- Config -------------------- */
const BALANCE_OPTIONS = [1, 5, 10, 100, 1000];
const QUICK_QTY = [1, 5, 10, 20, 50, 100];

const TIMER_OPTIONS = [
    { id: "30s", label: "30s", kind: "sec", value: 30 },
    { id: "1m", label: "1m", kind: "min", value: 1 },
    { id: "3m", label: "3m", kind: "min", value: 3 },
    { id: "5m", label: "5m", kind: "min", value: 5 },
    { id: "10m", label: "10m", kind: "min", value: 10 },
    { id: "1h", label: "1h", kind: "hour", value: 1 },
];

// Lock window seconds per timer
const LOCK_WINDOW_SEC = {
    "30s": 10,
    "1m": 10,
    "3m": 10,
    "5m": 10,
    "10m": 10,
    "1h": 10,
};
const getLockWindowSec = (timerId) => LOCK_WINDOW_SEC[timerId] ?? 10;

const IST_OFFSET_MS = 330 * 60 * 1000; // fixed offset (+05:30), no DST

const clampInt = (n, min = 1, max = 1_000_000_000) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return min;
    return Math.max(min, Math.min(max, Math.trunc(x)));
};

const formatINR = (n) =>
    `${Number(n || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
    })}`;

const formatTimeIST_12H = (ts) => {
    const s = new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    }).format(new Date(ts));

    return s.replace(/\u202f/g, " ").replace(/\u00a0/g, " ");
};

const formatCountdown = (sec) => {
    const s = Math.max(0, Number(sec) || 0);
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    const pad2 = (n) => String(n).padStart(2, "0");
    if (hh > 0) return `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`;
    return `${pad2(mm)}:${pad2(ss)}`;
};

// Compute NEXT boundary timestamp for selected timer (using IST wall-clock)
const computeNextBoundaryTs = (nowTs, timerId) => {
    const t = TIMER_OPTIONS.find((x) => x.id === timerId) || TIMER_OPTIONS[0];

    const istTs = nowTs + IST_OFFSET_MS;
    const d = new Date(istTs);

    const Y = d.getUTCFullYear();
    const M = d.getUTCMonth();
    const D = d.getUTCDate();
    const h = d.getUTCHours();
    const m = d.getUTCMinutes();
    const s = d.getUTCSeconds();

    if (t.kind === "sec" && t.value === 30) {
        const boundarySec = s < 30 ? 30 : 60;
        const base = Date.UTC(Y, M, D, h, m, 0, 0);
        const istBoundary = base + boundarySec * 1000;
        return istBoundary - IST_OFFSET_MS;
    }

    if (t.kind === "min") {
        const step = t.value;
        const nextMin = m - (m % step) + step;
        const baseHour = Date.UTC(Y, M, D, h, 0, 0, 0);
        const istBoundary = baseHour + nextMin * 60 * 1000;
        return istBoundary - IST_OFFSET_MS;
    }

    const istBoundary = Date.UTC(Y, M, D, h + 1, 0, 0, 0);
    return istBoundary - IST_OFFSET_MS;
};

/* --------------------------------------------
  Loader Ring (progress 0..1)
--------------------------------------------- */
const LoaderRing = ({
    size = 92,
    stroke = 10,
    progress = 0,
    color = "#ef4444",
    track = "rgba(239,68,68,0.20)",
}) => {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const p = Math.max(0, Math.min(1, progress));
    const dashOffset = c * (1 - p);

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={track}
                strokeWidth={stroke}
            />
            <circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={color}
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={c}
                strokeDashoffset={dashOffset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
        </svg>
    );
};

/* =========================================================
   ✅ BEAUTIFUL WIN/LOSS MODAL (TradingView / Gurutrade style)
   - Does NOT change your UI layout, only overlays on top.
========================================================= */
const TradeResultModal = ({ open, onClose, data, isDarkMode }) => {
    if (!data) return null;

    const isWin = data.type === "win";

    const overlay = {
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
        background: isDarkMode ? "rgba(0,0,0,0.55)" : "rgba(15,23,42,0.35)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
    };

    const card = {
        width: "min(520px, 100%)",
        borderRadius: 18,
        overflow: "hidden",
        border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"
            }`,
        background: isDarkMode
            ? "linear-gradient(180deg, rgba(17,26,39,0.92) 0%, rgba(11,15,23,0.92) 100%)"
            : "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
        boxShadow: isDarkMode
            ? "0 20px 70px rgba(0,0,0,0.65)"
            : "0 20px 70px rgba(15,23,42,0.18)",
    };

    const header = {
        padding: "14px 16px",
        borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"
            }`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        background: isWin
            ? isDarkMode
                ? "linear-gradient(90deg, rgba(16,185,129,0.22) 0%, rgba(17,26,39,0.0) 70%)"
                : "linear-gradient(90deg, rgba(16,185,129,0.18) 0%, rgba(255,255,255,0.0) 70%)"
            : isDarkMode
                ? "linear-gradient(90deg, rgba(239,68,68,0.22) 0%, rgba(17,26,39,0.0) 70%)"
                : "linear-gradient(90deg, rgba(239,68,68,0.18) 0%, rgba(255,255,255,0.0) 70%)",
    };

    const titleWrap = { display: "flex", flexDirection: "column", gap: 3 };

    const title = {
        fontSize: 14,
        fontWeight: 1000,
        letterSpacing: 0.3,
        color: isDarkMode ? "#e5e7eb" : "#0f172a",
        display: "flex",
        alignItems: "center",
        gap: 10,
    };

    const subtitle = {
        fontSize: 11,
        fontWeight: 850,
        color: isDarkMode ? "#94a3b8" : "#64748b",
    };

    const statusPill = {
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 1000,
        border: `1px solid ${isWin ? "rgba(16,185,129,0.40)" : "rgba(239,68,68,0.40)"}`,
        background: isWin ? "rgba(16,185,129,0.14)" : "rgba(239,68,68,0.14)",
        color: isWin ? "#10b981" : "#ef4444",
        whiteSpace: "nowrap",
    };

    const body = { padding: 16 };

    const grid = {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 10,
        marginTop: 12,
    };

    const metric = {
        borderRadius: 14,
        padding: "10px 12px",
        border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"
            }`,
        background: isDarkMode ? "rgba(15,23,42,0.35)" : "rgba(255,255,255,0.70)",
    };

    const metricLabel = {
        fontSize: 11,
        fontWeight: 900,
        color: isDarkMode ? "#94a3b8" : "#64748b",
        marginBottom: 6,
    };

    const metricValue = {
        fontSize: 15,
        fontWeight: 1000,
        color: isDarkMode ? "#e5e7eb" : "#0f172a",
    };

    const highlight = {
        marginTop: 12,
        borderRadius: 16,
        padding: 14,
        border: `1px solid ${isWin ? "rgba(16,185,129,0.35)" : "rgba(239,68,68,0.35)"}`,
        background: isWin
            ? isDarkMode
                ? "linear-gradient(180deg, rgba(16,185,129,0.14) 0%, rgba(15,23,42,0.20) 100%)"
                : "linear-gradient(180deg, rgba(16,185,129,0.12) 0%, rgba(255,255,255,0.75) 100%)"
            : isDarkMode
                ? "linear-gradient(180deg, rgba(239,68,68,0.14) 0%, rgba(15,23,42,0.20) 100%)"
                : "linear-gradient(180deg, rgba(239,68,68,0.12) 0%, rgba(255,255,255,0.75) 100%)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
    };

    const highlightLeft = { display: "flex", flexDirection: "column", gap: 6 };
    const highlightTitle = {
        fontSize: 12,
        fontWeight: 1000,
        color: isDarkMode ? "#e5e7eb" : "#0f172a",
    };
    const highlightNote = {
        fontSize: 11,
        fontWeight: 850,
        color: isDarkMode ? "#94a3b8" : "#64748b",
    };
    const highlightValue = {
        fontSize: 20,
        fontWeight: 1100,
        color: isWin ? "#10b981" : "#ef4444",
    };

    const footer = {
        padding: "12px 16px 16px",
        display: "flex",
        gap: 10,
    };

    const btn = {
        flex: 1,
        height: 44,
        borderRadius: 14,
        border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"
            }`,
        background: isDarkMode ? "rgba(17,26,39,0.65)" : "rgba(255,255,255,0.85)",
        color: isDarkMode ? "#e5e7eb" : "#0f172a",
        fontWeight: 1000,
        cursor: "pointer",
    };

    const primaryBtn = {
        ...btn,
        border: `1px solid ${isWin ? "rgba(16,185,129,0.45)" : "rgba(239,68,68,0.45)"}`,
        background: isWin ? "rgba(16,185,129,0.14)" : "rgba(239,68,68,0.14)",
        color: isWin ? "#10b981" : "#ef4444",
    };

    return (
        <AnimatePresence>
            {open ? (
                <motion.div
                    style={overlay}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onMouseDown={(e) => {
                        // click outside to close
                        if (e.target === e.currentTarget) onClose?.();
                    }}
                >
                    <motion.div
                        style={card}
                        initial={{ opacity: 0, y: 14, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 14, scale: 0.98 }}
                        transition={{ type: "spring", stiffness: 380, damping: 28 }}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div style={header}>
                            <div style={titleWrap}>
                                <div style={title}>
                                    <span style={statusPill}>{isWin ? "WIN" : "LOSS"}</span>
                                    <span>
                                        {data.entity?.toUpperCase() || "TRADE"} • {data.period || "—"} •
                                        #{data.period_id || "—"}
                                    </span>
                                </div>
                                <div style={subtitle}>
                                    Side: <b style={{ color: isDarkMode ? "#e5e7eb" : "#0f172a" }}>
                                        {(data.result_side || "—").toUpperCase()}
                                    </b>{" "}
                                    {data.trades_count ? `• Trades: ${data.trades_count}` : ""}
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 12,
                                    border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"
                                        }`,
                                    background: isDarkMode ? "rgba(17,26,39,0.55)" : "rgba(255,255,255,0.75)",
                                    color: isDarkMode ? "#e5e7eb" : "#0f172a",
                                    fontWeight: 1000,
                                    cursor: "pointer",
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={body}>
                            <div style={grid}>
                                <div style={metric}>
                                    <div style={metricLabel}>Trade Amount</div>
                                    <div style={metricValue}>{formatINR(data.trade_amount)}</div>
                                </div>

                                <div style={metric}>
                                    <div style={metricLabel}>Tax</div>
                                    <div style={metricValue}>{formatINR(data.tax)}</div>
                                </div>

                                <div style={metric}>
                                    <div style={metricLabel}>Actual Amount</div>
                                    <div style={metricValue}>{formatINR(data.actual_amount)}</div>
                                </div>

                                <div style={metric}>
                                    <div style={metricLabel}>{isWin ? "Win Amount" : "Loss Amount"}</div>
                                    <div style={metricValue}>
                                        {formatINR(isWin ? data.win_amount : data.loss_amount)}
                                    </div>
                                </div>
                            </div>

                            <div style={highlight}>
                                <div style={highlightLeft}>
                                    <div style={highlightTitle}>{isWin ? "Profit" : "Total Loss"}</div>
                                    <div style={highlightNote}>
                                        {isWin
                                            ? "Profit = Win Amount - Actual Amount"
                                            : "Loss = Sum of matched pending trade amounts"}
                                    </div>
                                </div>

                                <div style={highlightValue}>
                                    {isWin ? `+${formatINR(data.profit)}` : `-${formatINR(data.loss_amount)}`}
                                </div>
                            </div>
                        </div>

                        <div style={footer}>
                            <button type="button" onClick={onClose} style={btn}>
                                Close
                            </button>
                            <button type="button" onClick={onClose} style={primaryBtn}>
                                OK
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
};

const TradeScreen = ({ theme = "dark" }) => {
    const { entity_id } = useParams();
    const isDarkMode = theme === "dark";
    const tvTheme = isDarkMode ? "dark" : "light";

    /* -------------------- ✅ Coin Data -------------------- */
    const [coinData, setCoinData] = useState({
        entity_id: "",
        entity_name: "",
        symbol: "BINANCE:BTCUSDT",
        period_30s: 0,
        period_1m: 0,
        period_3m: 0,
        period_5m: 0,
        period_10m: 0,
        period_1h: 0,
        image: "",
        current_price: "0",
        change_percentage: "0",
    });

    const [tvSymbol, setTvSymbol] = useState("BINANCE:BTCUSDT");

    // Tabs
    const [activeTab, setActiveTab] = useState("result");

    // Timer
    const [activeTimer, setActiveTimer] = useState("30s");
    const [chartReloadKey, setChartReloadKey] = useState(0);

    // Amount system
    const [selectedBalance, setSelectedBalance] = useState(1);
    const [quantityInput, setQuantityInput] = useState("1");

    // Loading (trade action)
    const [isLoading, setIsLoading] = useState(false);

    // Pagination
    const PAGE_SIZE = 7;
    const [resultPage, setResultPage] = useState(1);
    const [myTradesPage, setMyTradesPage] = useState(1);

    // Data
    const [tradeResults, setTradeResults] = useState([]);
    const [myTrades, setMyTrades] = useState([]);

    /* -------------------- ✅ NEW: Result Modal State -------------------- */
    const [resultModalOpen, setResultModalOpen] = useState(false);
    const [resultModalData, setResultModalData] = useState(null);

    const openResultModal = useCallback((payload) => {
        setResultModalData(payload);
        setResultModalOpen(true);
    }, []);


    const closeResultModal = () => {
        const tf = activeTimer;

        FetchTradeResult(tf);
        FetchMyTrades(tf);

        GetCoinData(true);
        FetchProfile();

        setResultModalOpen(false);

        setTimeout(() => setResultModalData(null), 250);
    };


    const [nowTs, setNowTs] = useState(() => Date.now());
    const rafRef = useRef(null);

    const getNowMs = useCallback(() => Date.now(), []);

    useEffect(() => {
        const tick = () => {
            setNowTs(getNowMs());
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [getNowMs]);

    /* -------------------- ✅ Period Engine (local time based) -------------------- */
    const [periodEndTs, setPeriodEndTs] = useState(() =>
        computeNextBoundaryTs(Date.now(), "30s")
    );
    const [currentPeriodId, setCurrentPeriodId] = useState("");

    /* -------------------- ✅ COIN DATA API -------------------- */
    const GetCoinData = useCallback(async (is_auto_refresh = false) => {
        try {
            const response = await axios.post(`${BASE_API_URL}/trade/coin-data`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                entity_id,
            });

            const payload = response?.data;

            if (payload?.error) {
                toast.error(payload?.msg || "Failed to fetch coin data", {
                    position: "bottom-center",
                });
                return;
            }

            if (!payload?.has_entity || !payload?.data) {
                toast.error("Entity not found", { position: "bottom-center" });
                return;
            }

            const d = payload.data;

            setCurrentPeriodId(d[`period_${activeTimer}`]);

            if (!is_auto_refresh) {
                setCoinData(d);
                setTvSymbol(d?.symbol || "BINANCE:BTCUSDT");
                setChartReloadKey(Date.now());
            }

        } catch (error) {
            console.error("Error fetching coin data:", error);
            toast.error("Failed to fetch coin data", { position: "bottom-center" });
        }
    }, [entity_id, activeTimer]);

    const FetchProfile = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });

            const payload = response?.data;

            if (payload?.error) {
                toast.error(payload?.msg || "Failed to fetch coin data", {
                    position: "bottom-center",
                });
                return;
            } else {
                const balance = payload?.data?.balance;
                const usdt = payload?.data?.usdt;
                TriggerBalanceUpdate({ balance, usdt })
            }
        } catch (error) {
            console.error("Error fetching profile:", error);
            toast.error("Failed to fetch profile", { position: "bottom-center" });
        }
    };

    const FetchTradeResult = async (timeframe = activeTimer) => {
        try {
            const response = await axios.post(`${BASE_API_URL}/trade/trade-result`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                entity_id,
                period: timeframe,
            });

            const payload = response?.data;

            if (payload?.error) {
                toast.error(payload?.msg || "Failed to fetch coin data", {
                    position: "bottom-center",
                });
                return;
            }

            if (payload?.data) {
                setTradeResults(payload?.data);
            }
        } catch (error) {
            console.error("Error fetching coin data:", error);
            toast.error("Failed to fetch coin data", { position: "bottom-center" });
        }
    };

    /* -------------------- ✅ MY TRADES API -------------------- */
    const FetchMyTrades = useCallback(
        async (timeframe = activeTimer) => {
            try {
                const response = await axios.post(`${BASE_API_URL}/trade/my-trades`, {
                    token: localStorage.getItem("a5srf4wer7w1cd"),
                    entity_id: entity_id || "bitcoin",
                    period: timeframe || "30s",
                });
                const data = response?.data;

                if (data?.error) {
                    toast.error(data?.msg || "Failed to fetch trades", {
                        position: "bottom-center",
                    });
                    return;
                }

                if (Array.isArray(data?.data)) {
                    const mapped = data.data.map((x) => ({
                        trade_id: x.trade_id,
                        period_id: x.period_id,
                        period: x.period,
                        side: x.side,
                        status: (x.status || "").toLowerCase(), // pending | win | loss
                        trade_amount: Number(x.trade_amount ?? 0),
                        tax: Number(x.tax ?? 0),
                        actual_amount: Number(x.actual_amount ?? 0),
                        win_amount: Number(x.win_amount ?? 0),
                        trade_date: x.trade_date,
                    }));
                    setMyTrades(mapped);
                } else {
                    setMyTrades([]);
                }
            } catch (error) {
                console.error("Error fetching trades:", error);
                toast.error("Failed to fetch coin data", { position: "bottom-center" });
            }
        },
        [activeTimer, entity_id]
    );

    useEffect(() => {
        GetCoinData();
    }, [GetCoinData]);

    useEffect(() => {
        FetchTradeResult();
        FetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        FetchMyTrades();
    }, [FetchMyTrades]);

    /* ==================== ✅ FIX: Period end MUST advance ==================== */
    useEffect(() => {
        setPeriodEndTs(computeNextBoundaryTs(nowTs, activeTimer));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTimer]);

    useEffect(() => {
        if (nowTs >= periodEndTs) {
            const next = computeNextBoundaryTs(nowTs, activeTimer);
            if (next <= periodEndTs) {
                setPeriodEndTs(periodEndTs + 1000);
            } else {
                setPeriodEndTs(next);
            }
        }
    }, [nowTs, periodEndTs, activeTimer]);

    /* -------------------- Timer -> TradingView interval mapping -------------------- */
    const tvInterval = useMemo(() => {
        const map = {
            "30s": "1",
            "1m": "1",
            "3m": "1",
            "5m": "1",
            "10m": "1",
            "1h": "1",
        };
        return map[activeTimer] || "1";
    }, [activeTimer]);

    /* -------------------- Header price / change -------------------- */
    const priceNum = useMemo(() => Number(coinData?.current_price || 0), [coinData]);
    const changePctNum = useMemo(() => Number(coinData?.change_percentage || 0), [coinData]);
    const isChangeUp = changePctNum >= 0;

    const displayPairText = useMemo(() => {
        const name = coinData?.entity_name || "BTC";
        return `${name}/USD`;
    }, [coinData]);

    /* -------------------- Derived values -------------------- */
    const quantity = useMemo(() => {
        if (quantityInput === "") return 0;
        return clampInt(quantityInput, 1);
    }, [quantityInput]);

    const totalAmount = useMemo(() => {
        const total = Number(selectedBalance) * Number(quantity || 0);
        return Number.isFinite(total) ? total : 0;
    }, [selectedBalance, quantity]);

    const countdownSec = useMemo(() => {
        const diff = Math.floor((periodEndTs - nowTs) / 1000);
        return Math.max(0, diff);
    }, [periodEndTs, nowTs]);

    const lockWindowSec = useMemo(() => getLockWindowSec(activeTimer), [activeTimer]);
    const amountDisabled = useMemo(() => countdownSec <= lockWindowSec, [countdownSec, lockWindowSec]);

    const canTrade = useMemo(() => totalAmount > 0 && !isLoading && !amountDisabled, [
        totalAmount,
        isLoading,
        amountDisabled,
    ]);

    const lockProgress = useMemo(() => {
        if (!amountDisabled) return 0;
        const w = Math.max(1, lockWindowSec);
        return (w - Math.min(w, countdownSec)) / w;
    }, [amountDisabled, countdownSec, lockWindowSec]);

    /* -------------------- Actions -------------------- */
    const handleQuantityChange = (e) => {
        if (amountDisabled) return;
        const v = e.target.value;
        if (v === "") return setQuantityInput("");
        if (!/^\d+$/.test(v)) return;
        setQuantityInput(v);
    };

    const decQty = () => {
        if (amountDisabled) return;
        const q = quantityInput === "" ? 1 : clampInt(quantityInput, 1);
        setQuantityInput(String(Math.max(1, q - 1)));
    };

    const incQty = () => {
        if (amountDisabled) return;
        const q = quantityInput === "" ? 1 : clampInt(quantityInput, 1);
        setQuantityInput(String(q + 1));
    };

    const setQuickQty = (q) => {
        if (amountDisabled) return;
        setQuantityInput(String(q));
    };

    const clearAll = () => {
        if (amountDisabled) return;
        setQuantityInput("1");
        setSelectedBalance(1);
    };

    const handleTrade = async (side) => {
        if (!canTrade) return;

        setIsLoading(true);
        await new Promise((r) => setTimeout(r, 250));

        if (Number(totalAmount) < 1) {
            toast.error("Please enter a valid amount", {
                position: "top-center",
            });
            setIsLoading(false);
            return;
        }

        try {
            const response = await axios.post(`${BASE_API_URL}/trade/create-trade`, {
                entity_id,
                token: localStorage.getItem("a5srf4wer7w1cd"),
                amount: totalAmount,
                side,
                period: activeTimer,
            });
            const data = response?.data;

            if (data?.error) {
                toast.error(data?.error, { position: "bottom-center" });
            }
            if (data?.msg) {
                clearAll();
                FetchMyTrades(activeTimer);
                toast.success(data?.msg, {
                    duration: 1500,
                    position: "top-center",
                });
                if (data?.balance) {
                    TriggerBalanceUpdate({
                        balance: data?.balance,
                        usdt: data?.usdt
                    });
                }
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to place the trade", { position: "bottom-center" });
        }

        setIsLoading(false);
    };

    /* -------------------- Pagination -------------------- */
    const paginate = (arr, page, pageSize) => {
        const start = (page - 1) * pageSize;
        return arr.slice(start, start + pageSize);
    };

    const totalResultPages = Math.max(1, Math.ceil(tradeResults.length / PAGE_SIZE));
    const totalMyTradePages = Math.max(1, Math.ceil(myTrades.length / PAGE_SIZE));

    const resultPageData = useMemo(() => paginate(tradeResults, resultPage, PAGE_SIZE), [
        tradeResults,
        resultPage,
    ]);

    const myTradePageData = useMemo(() => paginate(myTrades, myTradesPage, PAGE_SIZE), [
        myTrades,
        myTradesPage,
    ]);

    /* =========================================================
       ✅ WebSocket improved handler (toast -> modal)
    ========================================================= */
    const socketRef = useRef(null);
    const myTradesRef = useRef([]);
    const activeTimerRef = useRef(activeTimer);
    const entityIdRef = useRef(entity_id);
    const processedRoundsRef = useRef(new Set());

    useEffect(() => {
        myTradesRef.current = myTrades;
    }, [myTrades]);

    useEffect(() => {
        activeTimerRef.current = activeTimer;
    }, [activeTimer]);

    useEffect(() => {
        entityIdRef.current = entity_id;
        processedRoundsRef.current = new Set();
    }, [entity_id]);

    const handleTradeProfitReleased = useCallback(
        (payload) => {
            try {
                const pEntity = String(payload?.entity_id || "");
                const pPeriod = String(payload?.period || "");
                const pPeriodId = String(payload?.period_id || "");
                const pSide = String(payload?.side || "").toLowerCase();

                if (!pEntity || pEntity !== String(entityIdRef.current || "")) return;
                if (!pPeriod || pPeriod !== String(activeTimerRef.current || "")) return;

                const roundKey = `${pEntity}__${pPeriod}__${pPeriodId}`;
                if (processedRoundsRef.current.has(roundKey)) return;

                const allTrades = Array.isArray(myTradesRef.current) ? myTradesRef.current : [];

                const pendingForRound = allTrades.filter((t) => {
                    const st = String(t?.status || "").toLowerCase();
                    if (st !== "pending") return false;

                    const tPeriod = String(t?.period || "");
                    const tPeriodId = String(t?.period_id || "");
                    return tPeriod === pPeriod && tPeriodId === pPeriodId;
                });

                if (pendingForRound.length === 0) {
                    FetchTradeResult(activeTimerRef.current);
                    FetchMyTrades(activeTimerRef.current);
                    GetCoinData(true);
                    FetchProfile();
                    return;
                }

                const winners = pendingForRound.filter(
                    (t) => String(t?.side || "").toLowerCase() === pSide
                );

                const sum = (arr, key) =>
                    arr.reduce((acc, x) => acc + Number(x?.[key] ?? 0), 0);

                if (winners.length > 0) {
                    // WIN aggregates (only winners)
                    const trade_amount = sum(winners, "trade_amount");
                    const tax = sum(winners, "tax");
                    const actual_amount = sum(winners, "actual_amount");
                    const win_amount = sum(winners, "win_amount");
                    const profit = Math.max(0, Number(win_amount) - Number(actual_amount));

                    openResultModal({
                        type: "win",
                        entity: pEntity,
                        period: pPeriod,
                        period_id: Number(pPeriodId),
                        result_side: pSide,
                        trades_count: winners.length,
                        trade_amount,
                        tax,
                        actual_amount,
                        win_amount,
                        loss_amount: 0,
                        profit,
                    });
                } else {
                    // LOSS aggregates (all matched pending are losses)
                    const trade_amount = sum(pendingForRound, "trade_amount");
                    const tax = sum(pendingForRound, "tax");
                    const actual_amount = sum(pendingForRound, "actual_amount");
                    const loss_amount = trade_amount;

                    openResultModal({
                        type: "loss",
                        entity: pEntity,
                        period: pPeriod,
                        period_id: Number(pPeriodId),
                        result_side: pSide,
                        trades_count: pendingForRound.length,
                        trade_amount,
                        tax,
                        actual_amount,
                        win_amount: 0,
                        loss_amount,
                        profit: 0,
                    });
                }

                processedRoundsRef.current.add(roundKey);
            } catch (e) {
                console.warn("trade_profit_released handler error:", e);
            }
        },
        [FetchMyTrades, openResultModal]
    );

    useEffect(() => {
        const socket = io(BASE_API_URL, {
            transports: ["websocket"],
            path: "/socket.io",
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000,
            timeout: 10000,
        });

        socketRef.current = socket;

        socket.on("connect", () => console.log("✅ connected:", socket.id));
        socket.on("disconnect", (reason) => console.log("❌ disconnected:", reason));
        socket.on("connect_error", (err) =>
            console.warn("⚠️ connect_error:", err?.message || err)
        );

        socket.on("trade_profit_released", handleTradeProfitReleased);

        return () => {
            try {
                socket.off("trade_profit_released", handleTradeProfitReleased);
                socket.disconnect();
            } catch (e) { }
            socketRef.current = null;
        };
    }, [handleTradeProfitReleased]);

    /* -------------------- Styles -------------------- */
    const pageStyles = {
        minHeight: "100vh",
        padding: "0 0 0.5rem 0",
        fontSize: "13px",
        color: isDarkMode ? "#e5e7eb" : "#2c3e50",
        background: isDarkMode ? "#0b0f17" : "#f8fafc",
    };

    const cardStyles = {
        background: isDarkMode
            ? "linear-gradient(180deg, #0f1520 0%, #0b1018 100%)"
            : "#ffffff",
        border: `1px solid ${isDarkMode ? "#1f2a3a" : "#e2e8f0"}`,
        borderRadius: "12px",
        padding: "0.7rem",
        marginBottom: "0.6rem",
    };

    const sectionTitle = {
        fontSize: 14,
        fontWeight: 900,
        marginBottom: 8,
    };

    const pillBtn = (active) => ({
        flex: 1,
        height: 34,
        borderRadius: 10,
        fontWeight: 900,
        fontSize: 13,
        cursor: amountDisabled ? "not-allowed" : "pointer",
        border: active ? "none" : `1px solid ${isDarkMode ? "#263244" : "#dbe3ef"}`,
        background: active ? "#f6c500" : isDarkMode ? "#121a26" : "#f1f5f9",
        color: active ? "#0b0f17" : isDarkMode ? "#e5e7eb" : "#0f172a",
        opacity: amountDisabled ? 0.55 : 1,
    });

    const qtyBtn = {
        width: 42,
        height: 42,
        borderRadius: 10,
        border: "none",
        cursor: amountDisabled ? "not-allowed" : "pointer",
        display: "grid",
        placeItems: "center",
        background: "#f6c500",
        color: "#0b0f17",
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        opacity: amountDisabled ? 0.55 : 1,
    };

    const quickQtyBtn = (active) => ({
        height: 32,
        padding: "0 14px",
        borderRadius: 10,
        fontWeight: 900,
        cursor: amountDisabled ? "not-allowed" : "pointer",
        whiteSpace: "nowrap",
        border: active ? "none" : `1px solid ${isDarkMode ? "#263244" : "#dbe3ef"}`,
        background: active ? "#f6c500" : isDarkMode ? "#121a26" : "#f1f5f9",
        color: active ? "#0b0f17" : isDarkMode ? "#e5e7eb" : "#0f172a",
        fontSize: 12,
        opacity: amountDisabled ? 0.55 : 1,
    });

    const tabBtn = (active) => ({
        padding: "10px 0",
        background: "none",
        border: "none",
        color: active
            ? isDarkMode
                ? "#60a5fa"
                : "#3b82f6"
            : isDarkMode
                ? "#94a3b8"
                : "#64748b",
        borderBottom: `2px solid ${active ? (isDarkMode ? "#60a5fa" : "#3b82f6") : "transparent"
            }`,
        fontWeight: 900,
        cursor: "pointer",
        flex: 1,
        textAlign: "center",
        fontSize: 12,
    });

    const pagerBtn = (enabled) => ({
        flex: 1,
        padding: "9px 12px",
        borderRadius: 10,
        border: `1px solid ${isDarkMode ? "#2a374b" : "#dbe3ef"}`,
        background: enabled ? (isDarkMode ? "#111a27" : "#ffffff") : "transparent",
        color: isDarkMode ? "#e5e7eb" : "#0f172a",
        fontWeight: 900,
        cursor: enabled ? "pointer" : "not-allowed",
        opacity: enabled ? 1 : 0.5,
    });

    const badge = (bg, fg) => ({
        padding: "4px 10px",
        borderRadius: 999,
        background: bg,
        color: fg,
        fontWeight: 900,
        fontSize: 11,
        lineHeight: 1,
    });

    const resultRowBg = (result) => {
        const isUp = result === "up";
        const bgDark = isUp ? "rgba(16,185,129,0.14)" : "rgba(239,68,68,0.14)";
        const bgLight = isUp ? "rgba(16,185,129,0.10)" : "rgba(239,68,68,0.10)";
        return isDarkMode ? bgDark : bgLight;
    };

    const compactItem = (leftColor, bg) => ({
        padding: "9px 10px",
        marginBottom: 8,
        background: bg,
        borderRadius: 10,
        border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)"
            }`,
        borderLeft: `4px solid ${leftColor}`,
    });

    const getWinLossBadge = (status) => {
        if (status === "pending") {
            return badge(
                isDarkMode ? "rgba(148,163,184,0.14)" : "rgba(100,116,139,0.12)",
                isDarkMode ? "#cbd5e1" : "#334155"
            );
        }
        if (status === "win") return badge("rgba(16,185,129,0.14)", "#10b981");
        return badge("rgba(239,68,68,0.14)", "#ef4444");
    };

    const timerContainer = {
        margin: "0.6rem 0.85rem",
        borderRadius: 14,
        padding: "10px 10px",
        border: `1px solid ${amountDisabled
            ? isDarkMode
                ? "rgba(239,68,68,0.5)"
                : "rgba(239,68,68,0.35)"
            : isDarkMode
                ? "#1f2a3a"
                : "#e2e8f0"
            }`,
        background: amountDisabled
            ? isDarkMode
                ? "linear-gradient(180deg, rgba(239,68,68,0.20) 0%, rgba(11,15,23,1) 100%)"
                : "linear-gradient(180deg, rgba(239,68,68,0.14) 0%, rgba(255,255,255,1) 100%)"
            : isDarkMode
                ? "linear-gradient(180deg, rgba(96,165,250,0.12) 0%, rgba(11,15,23,1) 100%)"
                : "linear-gradient(180deg, rgba(59,130,246,0.10) 0%, rgba(255,255,255,1) 100%)",
    };

    const timerTab = (active) => {
        const danger = amountDisabled;
        const activeBg = danger
            ? isDarkMode
                ? "rgba(239,68,68,0.22)"
                : "rgba(239,68,68,0.16)"
            : isDarkMode
                ? "rgba(96,165,250,0.22)"
                : "rgba(59,130,246,0.16)";

        const activeBorder = danger ? "#ef4444" : isDarkMode ? "#93c5fd" : "#2563eb";
        const activeText = danger ? "#ef4444" : isDarkMode ? "#93c5fd" : "#2563eb";

        return {
            flex: 1,
            height: 42,
            borderRadius: 12,
            fontWeight: 950,
            fontSize: 12,
            cursor: "pointer",
            border: active
                ? `1px solid ${activeBorder}`
                : `1px solid ${isDarkMode ? "#263244" : "#dbe3ef"}`,
            background: active
                ? activeBg
                : isDarkMode
                    ? "rgba(18,26,38,0.75)"
                    : "rgba(241,245,249,0.9)",
            color: active ? activeText : isDarkMode ? "#e5e7eb" : "#0f172a",
            transition: "all 160ms ease",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1.1,
            gap: 4,
        };
    };

    const countdownPill = {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "7px 10px",
        borderRadius: 12,
        border: `1px solid ${amountDisabled
            ? isDarkMode
                ? "rgba(239,68,68,0.6)"
                : "rgba(239,68,68,0.45)"
            : isDarkMode
                ? "rgba(147,197,253,0.35)"
                : "rgba(37,99,235,0.25)"
            }`,
        background: amountDisabled
            ? isDarkMode
                ? "rgba(239,68,68,0.16)"
                : "rgba(239,68,68,0.12)"
            : isDarkMode
                ? "rgba(96,165,250,0.16)"
                : "rgba(59,130,246,0.10)",
        color: amountDisabled ? "#ef4444" : isDarkMode ? "#93c5fd" : "#2563eb",
        fontWeight: 950,
        fontSize: 12,
    };

    const amountPanelOverlay = {
        position: "absolute",
        inset: 0,
        borderRadius: 12,
        background: isDarkMode ? "rgba(11,15,23,0.70)" : "rgba(248,250,252,0.75)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 14,
        textAlign: "center",
        color: isDarkMode ? "#e5e7eb" : "#0f172a",
        fontWeight: 900,
        border: `1px solid ${isDarkMode ? "rgba(239,68,68,0.35)" : "rgba(239,68,68,0.25)"
            }`,
    };

    const timeBoxStyle = {
        background: isDarkMode ? "#223044" : "#e2e8f0",
        padding: "6px 10px",
        borderRadius: 10,
        fontSize: 11,
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: isDarkMode ? "#e5e7eb" : "#0f172a",
        fontWeight: 900,
        whiteSpace: "nowrap",
    };

    const currentTimeStr = useMemo(() => formatTimeIST_12H(nowTs), [nowTs]);
    const nextBoundaryStr = useMemo(() => formatTimeIST_12H(periodEndTs), [periodEndTs]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={pageStyles}
        >
            {/* ✅ Modal Overlay (does not change your UI) */}
            <TradeResultModal
                open={resultModalOpen}
                onClose={closeResultModal}
                data={resultModalData}
                isDarkMode={isDarkMode}
            />

            {/* Header */}
            <div
                style={{
                    padding: "0.65rem 0.85rem",
                    borderBottom: `1px solid ${isDarkMode ? "#1f2a3a" : "#e2e8f0"}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {coinData?.image ? (
                        <img
                            src={coinData.image}
                            alt={coinData.entity_name || "coin"}
                            style={{ width: 34, height: 34, borderRadius: 999 }}
                        />
                    ) : null}

                    <div>
                        <div style={{ fontSize: 16, fontWeight: 900 }}>{displayPairText}</div>

                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontWeight: 900 }}>
                                ${Number.isFinite(priceNum) ? priceNum.toLocaleString() : "0"}
                            </span>
                            <span
                                style={badge(
                                    isChangeUp
                                        ? "rgba(16,185,129,0.16)"
                                        : "rgba(239,68,68,0.16)",
                                    isChangeUp ? "#10b981" : "#ef4444"
                                )}
                            >
                                {isChangeUp ? "+" : ""}
                                {changePctNum === 0
                                    ? 0
                                    : Math.abs(changePctNum) < 0.01
                                        ? Math.sign(changePctNum) * 0.01
                                        : Number(changePctNum.toFixed(2))}
                                %
                            </span>
                        </div>
                    </div>
                </div>

                <div style={timeBoxStyle}>
                    <Clock size={14} />
                    <span>{currentTimeStr}</span>
                </div>
            </div>

            {/* TIMER (tab style) */}
            <div style={timerContainer}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                    }}
                >
                    <div
                        style={{
                            fontWeight: 950,
                            fontSize: 13,
                            color: isDarkMode ? "#e5e7eb" : "#0f172a",
                        }}
                    >
                        Select Timer
                    </div>

                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <div style={countdownPill}>
                            <Clock size={14} />
                            {formatCountdown(countdownSec)}
                        </div>
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    {TIMER_OPTIONS.map((t) => {
                        const keyMap = {
                            "30s": "period_30s",
                            "1m": "period_1m",
                            "3m": "period_3m",
                            "5m": "period_5m",
                            "10m": "period_10m",
                            "1h": "period_1h",
                        };
                        const apiKey = keyMap[t.id];
                        const pv = Number(coinData?.[apiKey] ?? 0);

                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => {
                                    setActiveTimer(t.id);
                                    setCurrentPeriodId(pv);
                                    FetchTradeResult(t.id);
                                    FetchMyTrades(t.id);
                                }}
                                style={timerTab(activeTimer === t.id)}
                            >
                                <span>{t.label}</span>
                            </button>
                        );
                    })}
                </div>

                <div
                    style={{
                        marginTop: 8,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: 11,
                        color: amountDisabled ? "#ef4444" : isDarkMode ? "#94a3b8" : "#64748b",
                        fontWeight: 800,
                    }}
                >
                    <span>
                        Period ID:{" "}
                        <span
                            style={{
                                color: isDarkMode ? "#e5e7eb" : "#0f172a",
                                fontWeight: 950,
                            }}
                        >
                            {currentPeriodId}
                        </span>
                    </span>

                    <span>
                        Next:{" "}
                        <span
                            style={{
                                color: isDarkMode ? "#e5e7eb" : "#0f172a",
                                fontWeight: 950,
                            }}
                        >
                            {nextBoundaryStr}
                        </span>
                    </span>
                </div>
            </div>

            {/* Chart */}
            <div
                style={{
                    height: 260,
                    margin: "0.6rem 0.85rem",
                    ...cardStyles,
                    padding: 0,
                    overflow: "hidden",
                }}
            >
                <TradingViewAdvancedChart
                    key={`${tvSymbol}_${tvTheme}_${tvInterval}_${chartReloadKey}`}
                    symbol={tvSymbol || "BINANCE:BTCUSDT"}
                    theme={tvTheme}
                    height={260}
                    interval={tvInterval}
                />
            </div>

            {/* Amount Panel */}
            <div style={{ margin: "0.6rem 0.85rem", ...cardStyles, position: "relative" }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 10,
                    }}
                >
                    <div style={sectionTitle}>Balance</div>
                    <div
                        style={{
                            fontSize: 12,
                            fontWeight: 950,
                            color: isDarkMode ? "#94a3b8" : "#64748b",
                        }}
                    >
                        Period ID:{" "}
                        <span style={{ color: isDarkMode ? "#e5e7eb" : "#0f172a" }}>
                            {currentPeriodId}
                        </span>
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                    {BALANCE_OPTIONS.map((b) => (
                        <button
                            key={b}
                            type="button"
                            onClick={() => (!amountDisabled ? setSelectedBalance(b) : null)}
                            style={pillBtn(selectedBalance === b)}
                            disabled={amountDisabled}
                        >
                            {b}
                        </button>
                    ))}
                </div>

                <div style={{ ...sectionTitle, marginTop: 10 }}>Quantity</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button type="button" onClick={() => { }} style={{ display: "none" }} />
                    <button type="button" onClick={decQty} style={qtyBtn} disabled={amountDisabled}>
                        <Minus size={16} />
                    </button>

                    <div
                        style={{
                            flex: 1,
                            height: 42,
                            borderRadius: 10,
                            background: isDarkMode ? "#1b2636" : "#f1f5f9",
                            border: `1px solid ${isDarkMode ? "#283548" : "#dbe3ef"}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 10px",
                            opacity: amountDisabled ? 0.55 : 1,
                        }}
                    >
                        <input
                            value={quantityInput}
                            onChange={handleQuantityChange}
                            inputMode="numeric"
                            disabled={amountDisabled}
                            style={{
                                width: "100%",
                                textAlign: "center",
                                fontSize: 18,
                                fontWeight: 950,
                                color: isDarkMode ? "#e5e7eb" : "#0f172a",
                                background: "transparent",
                                border: "none",
                                outline: "none",
                            }}
                        />
                    </div>

                    <button type="button" onClick={incQty} style={qtyBtn} disabled={amountDisabled}>
                        <Plus size={16} />
                    </button>
                </div>

                {/* Quick Quantity */}
                <div
                    style={{
                        display: "flex",
                        gap: 8,
                        marginTop: 10,
                        flexWrap: "nowrap",
                        overflowX: "auto",
                        paddingBottom: 2,
                        WebkitOverflowScrolling: "touch",
                        opacity: amountDisabled ? 0.55 : 1,
                    }}
                >
                    {QUICK_QTY.map((q) => {
                        const isActive = Number(quantity) === Number(q);
                        return (
                            <button
                                key={q}
                                type="button"
                                onClick={() => setQuickQty(q)}
                                style={quickQtyBtn(isActive)}
                                disabled={amountDisabled}
                            >
                                X{q}
                            </button>
                        );
                    })}
                </div>

                {/* Clear / Reset */}
                <button
                    type="button"
                    onClick={clearAll}
                    disabled={amountDisabled}
                    style={{
                        marginTop: 10,
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: 12,
                        border: `1px solid ${isDarkMode ? "#2a374b" : "#dbe3ef"}`,
                        background: "transparent",
                        color: isDarkMode ? "#e5e7eb" : "#0f172a",
                        fontWeight: 900,
                        cursor: amountDisabled ? "not-allowed" : "pointer",
                        fontSize: 12,
                        opacity: amountDisabled ? 0.55 : 1,
                    }}
                >
                    Clear / Reset
                </button>

                {/* Total */}
                <div
                    style={{
                        marginTop: 10,
                        borderRadius: 12,
                        padding: 12,
                        border: "1px solid #f6c500",
                        background: "rgba(246, 197, 0, 0.06)",
                        textAlign: "center",
                        opacity: amountDisabled ? 0.55 : 1,
                    }}
                >
                    <div style={{ fontSize: 18, fontWeight: 950, color: "#f6c500" }}>
                        Total amount {formatINR(totalAmount)}
                    </div>
                    <div
                        style={{
                            fontSize: 11,
                            color: isDarkMode ? "#94a3b8" : "#64748b",
                            marginTop: 4,
                        }}
                    >
                        {selectedBalance} × {quantityInput === "" ? 0 : quantity} ={" "}
                        {Number(totalAmount).toFixed(2)}
                    </div>
                </div>

                {/* Up / Down */}
                <div
                    style={{
                        display: "flex",
                        gap: 10,
                        marginTop: 10,
                        opacity: amountDisabled ? 0.55 : 1,
                    }}
                >
                    <button
                        type="button"
                        onClick={() => handleTrade("up")}
                        disabled={!canTrade}
                        style={{
                            flex: 1,
                            height: 50,
                            borderRadius: 12,
                            border: "1px solid #22c55e",
                            background: "rgba(34, 197, 94, 0.10)",
                            color: isDarkMode ? "#e5e7eb" : "#0f172a",
                            fontWeight: 950,
                            fontSize: 14,
                            cursor: canTrade ? "pointer" : "not-allowed",
                            opacity: canTrade ? 1 : 0.6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                        }}
                    >
                        Up <ArrowUp size={20} />
                    </button>

                    <button
                        type="button"
                        onClick={() => handleTrade("down")}
                        disabled={!canTrade}
                        style={{
                            flex: 1,
                            height: 50,
                            borderRadius: 12,
                            border: "1px solid #ef4444",
                            background: "rgba(239, 68, 68, 0.10)",
                            color: isDarkMode ? "#e5e7eb" : "#0f172a",
                            fontWeight: 950,
                            fontSize: 14,
                            cursor: canTrade ? "pointer" : "not-allowed",
                            opacity: canTrade ? 1 : 0.6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                        }}
                    >
                        Down <ArrowDown size={20} />
                    </button>
                </div>

                {/* Overlay when disabled: BIG countdown + loader ring */}
                {amountDisabled && (
                    <div style={amountPanelOverlay}>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 10,
                            }}
                        >
                            <div
                                style={{
                                    position: "relative",
                                    width: 96,
                                    height: 96,
                                    display: "grid",
                                    placeItems: "center",
                                }}
                            >
                                <LoaderRing
                                    size={96}
                                    stroke={10}
                                    progress={lockProgress}
                                    color="#ef4444"
                                    track={
                                        isDarkMode
                                            ? "rgba(239,68,68,0.20)"
                                            : "rgba(239,68,68,0.18)"
                                    }
                                />
                                <div
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        display: "grid",
                                        placeItems: "center",
                                        fontSize: 28,
                                        fontWeight: 1000,
                                        color: "#ef4444",
                                    }}
                                >
                                    {countdownSec}
                                </div>
                            </div>

                            <div
                                style={{
                                    fontSize: 14,
                                    fontWeight: 950,
                                    color: isDarkMode ? "#e5e7eb" : "#0f172a",
                                }}
                            >
                                Trading locked
                            </div>

                            <div
                                style={{
                                    fontSize: 12,
                                    fontWeight: 850,
                                    color: isDarkMode ? "#94a3b8" : "#64748b",
                                }}
                            >
                                Next round starts at{" "}
                                <span style={{ color: isDarkMode ? "#e5e7eb" : "#0f172a" }}>
                                    {nextBoundaryStr}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Tabs: Trade Result / My Trades */}
            <div style={{ margin: "0.6rem 0.85rem", ...cardStyles }}>
                <div
                    style={{
                        display: "flex",
                        gap: 10,
                        marginBottom: 10,
                        borderBottom: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                    }}
                >
                    <button
                        type="button"
                        onClick={() => {
                            setActiveTab("result");
                            FetchTradeResult();
                        }}
                        style={tabBtn(activeTab === "result")}
                    >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <Check size={14} />
                            Trade Result
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setActiveTab("my");
                            FetchMyTrades();
                        }}
                        style={tabBtn(activeTab === "my")}
                    >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <Activity size={14} />
                            My Trades
                        </span>
                    </button>
                </div>

                {/* Trade Result */}
                {activeTab === "result" && (
                    <>
                        {resultPageData.map((r) => (
                            <motion.div
                                key={r.id}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                style={compactItem(
                                    r.result === "up" ? "#10b981" : "#ef4444",
                                    resultRowBg(r.result)
                                )}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: 10,
                                    }}
                                >
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            padding: "4px 10px",
                                            borderRadius: 999,
                                            background: isDarkMode
                                                ? "rgba(17,26,39,0.65)"
                                                : "rgba(255,255,255,0.7)",
                                            border: `1px solid ${isDarkMode
                                                ? "rgba(38,50,68,0.8)"
                                                : "rgba(219,227,239,0.9)"
                                                }`,
                                            fontWeight: 900,
                                            fontSize: 11,
                                            color: isDarkMode ? "#e5e7eb" : "#0f172a",
                                        }}
                                    >
                                        Period Id: {r.period_id}
                                    </span>

                                    <span
                                        style={badge(
                                            r.result === "up"
                                                ? "rgba(16,185,129,0.16)"
                                                : "rgba(239,68,68,0.16)",
                                            r.result === "up" ? "#10b981" : "#ef4444"
                                        )}
                                    >
                                        {r.result.toUpperCase()}
                                    </span>
                                </div>

                                <div
                                    style={{
                                        marginTop: 8,
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        color: isDarkMode ? "#94a3b8" : "#64748b",
                                        fontSize: 11,
                                        fontWeight: 800,
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <Clock size={12} />
                                        <span>
                                            {moment(r.trade_date).format("DD/MM/YYYY hh:mm:ss A")}
                                        </span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}

                        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                            <button
                                type="button"
                                onClick={() => setResultPage((p) => Math.max(1, p - 1))}
                                style={pagerBtn(resultPage > 1)}
                                disabled={resultPage <= 1}
                            >
                                Previous
                            </button>

                            <button
                                type="button"
                                onClick={() => setResultPage((p) => Math.min(totalResultPages, p + 1))}
                                style={pagerBtn(resultPage < totalResultPages)}
                                disabled={resultPage >= totalResultPages}
                            >
                                Next
                            </button>
                        </div>

                        <div
                            style={{
                                marginTop: 8,
                                textAlign: "center",
                                fontSize: 11,
                                color: isDarkMode ? "#94a3b8" : "#64748b",
                                fontWeight: 800,
                            }}
                        >
                            Page {resultPage} of {totalResultPages}
                        </div>
                    </>
                )}

                {/* My Trades (your UI unchanged) */}
                {activeTab === "my" && (
                    <>
                        {myTradePageData.map((t) => {
                            const st = (t.status || "").toLowerCase();

                            const leftColor =
                                st === "win" ? "#10b981" : st === "loss" ? "#ef4444" : "#94a3b8";

                            const bg =
                                st === "win"
                                    ? isDarkMode
                                        ? "rgba(16,185,129,0.10)"
                                        : "rgba(16,185,129,0.07)"
                                    : st === "loss"
                                        ? isDarkMode
                                            ? "rgba(239,68,68,0.10)"
                                            : "rgba(239,68,68,0.07)"
                                        : isDarkMode
                                            ? "rgba(148,163,184,0.10)"
                                            : "rgba(100,116,139,0.08)";

                            const showTradeDate = st !== "pending" && !!t.trade_date;
                            const showWinAmount = st === "win";
                            const showLossAmount = st === "loss";

                            const tradeDateStr = showTradeDate
                                ? moment(t.trade_date).format("DD/MM/YYYY hh:mm:ss A")
                                : "";

                            const side = (t.side || "").toLowerCase();
                            const sideLabel = side ? side.toUpperCase() : "—";
                            const sideBadgeStyle = badge(
                                side === "up" ? "rgba(34,197,94,0.16)" : "rgba(239,68,68,0.16)",
                                side === "up" ? "#22c55e" : "#ef4444"
                            );

                            return (
                                <motion.div
                                    key={t.trade_id}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    style={compactItem(leftColor, bg)}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 10,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                    padding: "4px 10px",
                                                    borderRadius: 999,
                                                    background: isDarkMode
                                                        ? "rgba(17,26,39,0.65)"
                                                        : "rgba(255,255,255,0.7)",
                                                    border: `1px solid ${isDarkMode
                                                        ? "rgba(38,50,68,0.8)"
                                                        : "rgba(219,227,239,0.9)"
                                                        }`,
                                                    fontWeight: 900,
                                                    fontSize: 11,
                                                    color: isDarkMode ? "#e5e7eb" : "#0f172a",
                                                }}
                                            >
                                                Period ID: {t.period_id}
                                            </span>

                                            <span style={sideBadgeStyle}>{sideLabel}</span>
                                        </div>

                                        <div
                                            style={{
                                                fontWeight: 950,
                                                fontSize: 12,
                                                color: isDarkMode ? "#e5e7eb" : "#0f172a",
                                            }}
                                        >
                                            {t.trade_amount}
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            marginTop: 8,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 10,
                                        }}
                                    >
                                        <span style={getWinLossBadge(st)}>{st.toUpperCase()}</span>

                                        {showWinAmount ? (
                                            <span style={badge("rgba(16,185,129,0.16)", "#10b981")}>
                                                WIN: {formatINR(t.win_amount)}
                                            </span>
                                        ) : null}

                                        {showLossAmount ? (
                                            <span style={badge("rgba(239,68,68,0.16)", "#ef4444")}>
                                                LOSS: {formatINR(t.trade_amount)}
                                            </span>
                                        ) : null}
                                    </div>

                                    <div
                                        style={{
                                            marginTop: 8,
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 8,
                                            color: isDarkMode ? "#94a3b8" : "#64748b",
                                            fontSize: 11,
                                            fontWeight: 800,
                                        }}
                                    >
                                        <span>
                                            Total:{" "}
                                            <span style={{ color: isDarkMode ? "#e5e7eb" : "#0f172a" }}>
                                                {formatINR(t.trade_amount)}
                                            </span>
                                        </span>
                                        <span>
                                            Tax:{" "}
                                            <span style={{ color: isDarkMode ? "#e5e7eb" : "#0f172a" }}>
                                                {formatINR(t.tax)}
                                            </span>
                                        </span>
                                        <span>
                                            Trade:{" "}
                                            <span style={{ color: isDarkMode ? "#e5e7eb" : "#0f172a" }}>
                                                {formatINR(t.actual_amount)}
                                            </span>
                                        </span>
                                    </div>

                                    {showTradeDate ? (
                                        <div
                                            style={{
                                                marginTop: 8,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 6,
                                                color: isDarkMode ? "#94a3b8" : "#64748b",
                                                fontSize: 11,
                                                fontWeight: 800,
                                            }}
                                        >
                                            <Clock size={12} />
                                            {tradeDateStr}
                                        </div>
                                    ) : null}
                                </motion.div>
                            );
                        })}

                        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                            <button
                                type="button"
                                onClick={() => setMyTradesPage((p) => Math.max(1, p - 1))}
                                style={pagerBtn(myTradesPage > 1)}
                                disabled={myTradesPage <= 1}
                            >
                                Previous
                            </button>

                            <button
                                type="button"
                                onClick={() => setMyTradesPage((p) => Math.min(totalMyTradePages, p + 1))}
                                style={pagerBtn(myTradesPage < totalMyTradePages)}
                                disabled={myTradesPage >= totalMyTradePages}
                            >
                                Next
                            </button>
                        </div>

                        <div
                            style={{
                                marginTop: 8,
                                textAlign: "center",
                                fontSize: 11,
                                color: isDarkMode ? "#94a3b8" : "#64748b",
                                fontWeight: 800,
                            }}
                        >
                            Page {myTradesPage} of {totalMyTradePages}
                        </div>
                    </>
                )}
            </div>
        </motion.div>
    );
};

export default TradeScreen;
